import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, TrendingUp, Shield, Wrench, CreditCard, Users, Clock, Zap, Truck, Monitor, Building2, Layers, ShoppingCart, Factory, Building, AlertTriangle, Home } from 'lucide-react';
import { mockTestimonials } from '../mock';

const AnimatedCounter = ({ end, suffix = '', prefix = '' }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const duration = 2000;
          const steps = 60;
          const increment = end / steps;
          let current = 0;
          const timer = setInterval(() => {
            current += increment;
            if (current >= end) {
              setCount(end);
              clearInterval(timer);
            } else {
              setCount(Math.floor(current));
            }
          }, duration / steps);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end]);

  return <span ref={ref}>{prefix}{count.toLocaleString()}{suffix}</span>;
};

const Homepage = () => {
  const [monthlyRevenue, setMonthlyRevenue] = useState('');

  const calculateLoan = () => {
    if (!monthlyRevenue || isNaN(monthlyRevenue)) return { loanAmount: 0, monthlyPayment: 0, isEligible: false };
    const revenue = parseFloat(monthlyRevenue);
    const minMonthlyRevenue = 100000 / 12;
    if (revenue < minMonthlyRevenue) return { loanAmount: 0, monthlyPayment: 0, isEligible: false };
    let loanAmount = revenue * 7.2;
    if (loanAmount > 200000) loanAmount = 200000;
    const monthlyPayment = loanAmount / 62;
    return { loanAmount, monthlyPayment, isEligible: true };
  };

  const { loanAmount, monthlyPayment, isEligible } = calculateLoan();

  return (
    <div className="bg-[#FAFAFA]">
      {/* ====== HERO ====== */}
      <section className="relative overflow-hidden bg-slate-950">
        {/* Animated background layers */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(17,94,89,0.3)_0%,_transparent_60%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_rgba(30,58,138,0.2)_0%,_transparent_60%)]" />
          <div className="absolute top-1/4 right-1/4 w-[600px] h-[600px] bg-teal-500/10 rounded-full blur-[150px] animate-float" />
          <div className="absolute bottom-1/3 left-1/3 w-[400px] h-[400px] bg-blue-500/8 rounded-full blur-[120px] animate-float" style={{ animationDelay: '3s' }} />
        </div>

        {/* Grid pattern overlay */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }} />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-8 pt-10 pb-20 md:pb-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left content */}
            <div>
              <div className="animate-fade-in-up">
                <div className="inline-flex items-center gap-2 bg-white/[0.07] backdrop-blur-md border border-white/10 rounded-full px-4 py-1.5 mb-8">
                  <div className="w-2 h-2 rounded-full bg-teal-400 animate-pulse" />
                  <span className="text-xs font-semibold text-slate-300 uppercase tracking-wider">Now Funding Nationwide</span>
                </div>
              </div>

              <h1 className="animate-fade-in-up delay-100 text-4xl sm:text-5xl lg:text-7xl font-bold text-white leading-[1.05] mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Cashflow<br />
                <span className="text-gradient" style={{ background: 'linear-gradient(135deg, #5eead4 0%, #3b82f6 50%, #a78bfa 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  Engineering
                </span>
              </h1>

              <p className="animate-fade-in-up delay-200 text-base md:text-lg text-slate-400 leading-relaxed mb-10 max-w-lg">
                Term loans, lines of credit, equipment financing, merchant processing, and intelligent management systems. Designed for growth minded businesses. Fast approvals. Expert guidance.
              </p>

              <div className="animate-fade-in-up delay-300 flex flex-wrap gap-4">
                <Link
                  to="/apply"
                  data-testid="hero-apply-button"
                  className="group inline-flex items-center gap-2 bg-white text-slate-900 font-semibold text-sm px-7 py-3.5 rounded-full hover:shadow-[0_0_30px_rgba(94,234,212,0.3)] transition-all duration-300"
                >
                  Apply for Financing
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/partner"
                  data-testid="hero-partner-button"
                  className="inline-flex items-center gap-2 bg-white/[0.07] backdrop-blur-sm border border-white/10 text-white font-semibold text-sm px-7 py-3.5 rounded-full hover:bg-white/[0.12] transition-all duration-300"
                >
                  Become a Referral Partner
                </Link>
              </div>
            </div>

            {/* Right - floating glass stats */}
            <div className="hidden lg:block relative">
              <div className="relative h-[420px] flex items-center justify-center">
                {/* 2x2 centered grid */}
                <div className="grid grid-cols-2 gap-4 w-full max-w-[460px]">
                  {/* Businesses Funded */}
                  <div className="bg-white/[0.07] backdrop-blur-xl border border-white/10 rounded-2xl p-7 animate-fade-in-up delay-200">
                    <div className="text-xs font-bold text-blue-400 uppercase tracking-wider mb-3">Businesses Funded</div>
                    <div className="text-5xl font-bold text-white" style={{ fontFamily: 'var(--font-heading)' }}>
                      <AnimatedCounter end={1000} suffix="+" />
                    </div>
                  </div>

                  {/* Avg Approval */}
                  <div className="bg-white/[0.07] backdrop-blur-xl border border-white/10 rounded-2xl p-7 animate-fade-in-up delay-300">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-teal-500/20 flex items-center justify-center">
                        <Zap className="h-6 w-6 text-teal-400" />
                      </div>
                      <div>
                        <div className="text-3xl font-bold text-white" style={{ fontFamily: 'var(--font-heading)' }}>24hrs</div>
                        <div className="text-xs text-slate-500">Avg. Approval</div>
                      </div>
                    </div>
                  </div>

                  {/* Satisfaction */}
                  <div className="bg-white/[0.07] backdrop-blur-xl border border-white/10 rounded-2xl p-7 animate-fade-in-up delay-400 col-span-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-xs font-bold text-amber-400 uppercase tracking-wider mb-3">Client Satisfaction</div>
                        <div className="text-5xl font-bold text-white" style={{ fontFamily: 'var(--font-heading)' }}>98%</div>
                      </div>
                      <div className="flex gap-0.5">
                        {[...Array(5)].map((_, j) => (
                          <svg key={j} className="w-5 h-5 text-amber-400 fill-current" viewBox="0 0 20 20">
                            <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                          </svg>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile stats row */}
          <div className="lg:hidden grid grid-cols-3 gap-3 mt-10">
            {[
              { label: 'Businesses Funded', value: '1,000+' },
              { label: 'Avg. Approval', value: '24hrs' },
              { label: 'Satisfaction', value: '98%' },
            ].map((s) => (
              <div key={s.label} className="bg-white/[0.07] backdrop-blur-sm border border-white/10 rounded-xl p-4 text-center">
                <div className="text-xl font-bold text-white" style={{ fontFamily: 'var(--font-heading)' }}>{s.value}</div>
                <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ====== RESTRUCTURE HIGHLIGHT ====== */}
      <section className="relative py-16 md:py-20 bg-[#115E59] overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
          backgroundSize: '40px 40px'
        }} />
        <div className="absolute top-0 right-0 w-96 h-96 bg-teal-300/10 rounded-full blur-[120px]" />
        <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center mb-12">
            <div className="lg:col-span-8">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                Restructure Your Financing and Debts at Better Terms to Significantly Improve Your Cashflow
              </h2>
              <p className="text-teal-100/80 text-base max-w-2xl">
                Replace expensive MCA debt and high interest obligations with predictable, long term repayment structures. Lower your monthly outflow, free up working capital, and put your business back in control.
              </p>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end">
              <Link
                to="/apply"
                data-testid="restructure-apply-cta"
                className="inline-flex items-center gap-2 bg-white text-[#115E59] font-semibold text-sm px-8 py-4 rounded-full hover:bg-teal-50 transition-all shadow-lg"
              >
                Start Restructuring <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          {/* Grow + Cash on Cash ROI */}
          <div className="border-t border-teal-400/20 pt-10">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-8">
                <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white leading-tight mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                  Grow Your Business With Financing That Makes Financial Sense
                </h3>
                <p className="text-teal-100/80 text-base max-w-2xl">
                  Improve your Cash on Cash ROI by using financing instead of operating cash. Preserve your reserves for opportunities, deploy borrowed capital for growth, and let your money work harder across every dollar spent.
                </p>
              </div>
              <div className="lg:col-span-4 flex lg:justify-end">
                <Link
                  to="/contact"
                  data-testid="grow-business-cta"
                  className="inline-flex items-center gap-2 border border-white/30 text-white font-semibold text-sm px-8 py-4 rounded-full hover:bg-white/10 transition-all"
                >
                  Talk to an Advisor <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ====== REAL ESTATE FEATURED BANNER ====== */}
      <section className="relative py-20 md:py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/1732414/pexels-photo-1732414.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1260" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-slate-950/80 to-slate-950/50" />
        </div>
        <div className="absolute inset-0 noise-overlay" />
        <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 bg-amber-500/15 border border-amber-400/20 rounded-full px-4 py-1.5 mb-6">
                <Building className="h-4 w-4 text-amber-300" />
                <span className="text-xs font-semibold text-amber-200 uppercase tracking-wider">Real Estate Finance</span>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-5" style={{ fontFamily: 'var(--font-heading)' }}>
                Real Estate Capital<br />From $250K to $2B
              </h2>
              <p className="text-base text-slate-300 leading-relaxed mb-8 max-w-lg">
                Financing projects across the United States and the Caribbean. Refinances, new developments, structured deals, acquisitions, and bridge financing for every asset type.
              </p>
              <div className="flex flex-wrap gap-3 mb-8">
                {['USA + Caribbean', 'Refis', 'New Development', 'Structured Deals', 'Bridge', '12+ Asset Types'].map((tag) => (
                  <span key={tag} className="text-[11px] font-semibold bg-white/10 backdrop-blur-sm text-white/80 px-3.5 py-1.5 rounded-full border border-white/10">{tag}</span>
                ))}
              </div>
              <div className="flex flex-wrap gap-4">
                <Link to="/solutions/real-estate" data-testid="re-hero-learn" className="inline-flex items-center gap-2 bg-amber-500 text-white font-semibold text-sm px-8 py-4 rounded-full hover:bg-amber-400 transition-all">
                  Explore Real Estate <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/apply" className="inline-flex items-center gap-2 border border-white/20 text-white font-semibold text-sm px-8 py-4 rounded-full hover:bg-white/10 transition-all">
                  Start Your Project <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5 hidden lg:grid grid-cols-2 gap-3">
              {[
                { val: '$2B', label: 'Max Project Size' },
                { val: '2', label: 'Markets (USA + Caribbean)' },
                { val: '12+', label: 'Asset Types' },
                { val: '6', label: 'Deal Structures' },
              ].map((s) => (
                <div key={s.label} className="bg-white/[0.06] backdrop-blur-sm border border-white/10 rounded-xl p-5 text-center">
                  <div className="text-2xl font-bold text-white mb-1" style={{ fontFamily: 'var(--font-heading)' }}>{s.val}</div>
                  <div className="text-[10px] text-slate-400 uppercase tracking-wider">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ====== SBA FEATURED BANNER ====== */}
      <section className="relative py-16 md:py-20 bg-[#0C2340] overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }} />
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-amber-500/5 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-red-500/5 rounded-full blur-[120px]" />
        <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-amber-500/15 border border-amber-400/20 rounded-full px-4 py-1.5 mb-6">
              <Shield className="h-4 w-4 text-amber-300" />
              <span className="text-xs font-semibold text-amber-200 uppercase tracking-wider">Now Available</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Now Offering SBA Backed Growth Capital
            </h2>
            <p className="text-base text-slate-400 max-w-2xl mx-auto mb-8">
              10 year financing solutions for grocery, agriculture, and U.S. manufacturing businesses designed to lower payments and support long term expansion.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            <Link to="/solutions/sba-grocery" data-testid="sba-grocery-banner" className="group p-6 rounded-2xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] hover:border-amber-400/30 transition-all">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500/15 flex items-center justify-center">
                  <ShoppingCart className="h-5 w-5 text-amber-300" />
                </div>
                <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'var(--font-heading)' }}>SBA Grocery Program</h3>
              </div>
              <p className="text-sm text-slate-400 leading-relaxed mb-3">Agriculture, food distribution, and grocery retail financing with 10 year terms.</p>
              <span className="text-xs font-semibold text-amber-400 group-hover:text-amber-300 flex items-center gap-1">
                Learn more <ArrowRight className="h-3 w-3 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
            <Link to="/solutions/sba-made-in-america" data-testid="sba-mia-banner" className="group p-6 rounded-2xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] hover:border-red-400/30 transition-all">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-red-500/15 flex items-center justify-center">
                  <Factory className="h-5 w-5 text-red-300" />
                </div>
                <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'var(--font-heading)' }}>SBA Made in America</h3>
              </div>
              <p className="text-sm text-slate-400 leading-relaxed mb-3">U.S. manufacturing financing to support production, scaling, and operational growth.</p>
              <span className="text-xs font-semibold text-red-400 group-hover:text-red-300 flex items-center gap-1">
                Learn more <ArrowRight className="h-3 w-3 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
          </div>
          <div className="text-center mt-8">
            <Link to="/apply" data-testid="sba-qualify-cta" className="inline-flex items-center gap-2 bg-amber-500 text-white font-semibold text-sm px-8 py-4 rounded-full hover:bg-amber-400 transition-colors">
              See If You Qualify <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ====== FORECLOSURE BAILOUT ====== */}
      <section className="relative py-20 md:py-24 bg-slate-950 overflow-hidden" data-testid="foreclosure-bailout-section">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,_rgba(220,38,38,0.12)_0%,_transparent_60%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_rgba(17,94,89,0.15)_0%,_transparent_60%)]" />
        </div>
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }} />
        <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left: content */}
            <div>
              <div className="inline-flex items-center gap-2 bg-red-500/15 border border-red-400/20 rounded-full px-4 py-1.5 mb-6">
                <AlertTriangle className="h-4 w-4 text-red-300" />
                <span className="text-xs font-semibold text-red-200 uppercase tracking-wider">Time Sensitive Program</span>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-5" style={{ fontFamily: 'var(--font-heading)' }}>
                Foreclosure Bailout<br />
                <span className="text-teal-400">Save Your Property</span>
              </h2>
              <p className="text-base text-slate-400 leading-relaxed mb-8 max-w-lg">
                Facing foreclosure or an upcoming auction date? Our bailout bridge loan covers the amount owed to stop the process immediately. The 40 year amortization reduces your payments, giving you breathing room to stabilize your property and finances.
              </p>

              {/* Key terms */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                <div className="bg-white/[0.05] border border-white/10 rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-9 h-9 rounded-lg bg-teal-500/20 flex items-center justify-center">
                      <Clock className="h-4 w-4 text-teal-400" />
                    </div>
                    <span className="text-xs font-bold text-teal-400 uppercase tracking-wider">Fast Funding</span>
                  </div>
                  <p className="text-sm text-slate-400">We move quickly to beat auction and foreclosure dates</p>
                </div>
                <div className="bg-white/[0.05] border border-white/10 rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-9 h-9 rounded-lg bg-blue-500/20 flex items-center justify-center">
                      <TrendingUp className="h-4 w-4 text-blue-400" />
                    </div>
                    <span className="text-xs font-bold text-blue-400 uppercase tracking-wider">Payment Structure</span>
                  </div>
                  <p className="text-sm text-slate-400">Amortized with a 2 year term and 40 year amortization schedule</p>
                </div>
              </div>

              <div className="bg-gradient-to-r from-teal-500/10 to-transparent border border-teal-400/15 rounded-xl p-5 mb-8">
                <p className="text-sm text-teal-200 leading-relaxed">
                  <span className="font-bold text-teal-300">Why 40 year amortization?</span> Even though the bridge loan only covers the payoff amount, the extended schedule significantly reduces your monthly payment, giving you immediate cash flow relief.
                </p>
              </div>

              <div className="flex flex-wrap gap-4">
                <Link to="/solutions/foreclosure-bailout" data-testid="foreclosure-apply-cta" className="inline-flex items-center gap-2 bg-red-500 text-white font-semibold text-sm px-8 py-4 rounded-full hover:bg-red-400 transition-all shadow-lg shadow-red-500/20">
                  Learn More <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/apply" className="inline-flex items-center gap-2 border border-white/20 text-white font-semibold text-sm px-8 py-4 rounded-full hover:bg-white/10 transition-all">
                  Apply Now <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>

            {/* Right: eligible property types */}
            <div>
              <div className="bg-white/[0.04] backdrop-blur-sm border border-white/10 rounded-2xl p-8 md:p-10">
                <h3 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'var(--font-heading)' }}>Eligible Property Types</h3>
                <p className="text-xs text-slate-500 mb-6 uppercase tracking-wider">Commercial &amp; Investment Properties Only</p>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { name: 'Single Family', icon: Home },
                    { name: '2 to 4 Units', icon: Building2 },
                    { name: 'Multifamily (5+ units)', icon: Layers },
                    { name: 'Office', icon: Building },
                    { name: 'Retail', icon: ShoppingCart },
                    { name: 'Mixed Use', icon: Building2 },
                    { name: 'Church / Special Use', icon: Shield },
                  ].map((prop) => (
                    <div key={prop.name} className="group flex items-center gap-3 bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-3.5 hover:border-teal-400/30 hover:bg-teal-400/[0.04] transition-all">
                      <prop.icon className="h-4 w-4 text-slate-500 group-hover:text-teal-400 transition-colors flex-shrink-0" />
                      <span className="text-sm font-medium text-slate-300 group-hover:text-white transition-colors">{prop.name}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 pt-5 border-t border-white/[0.06]">
                  <p className="text-xs text-slate-500 leading-relaxed">
                    This program is for commercial and investment properties only, including rental income properties. Primary residences are not eligible.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ====== SOLUTIONS BENTO ====== */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#115E59] mb-4 block">Our Solutions</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Cashflow Engineering Solutions
            </h2>
            <p className="text-slate-500 max-w-xl mx-auto">
              Eight focused solutions to engineer better cashflow at every stage.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
            {/* Term Loans - large with image */}
            <Link to="/solutions/term-loans" data-testid="bento-term-loans" className="md:col-span-7 group relative rounded-2xl overflow-hidden min-h-[340px] flex flex-col justify-end">
              <div className="absolute inset-0">
                <img src="https://images.pexels.com/photos/7256463/pexels-photo-7256463.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/80 to-slate-950/20" />
              </div>
              <div className="relative z-10 p-8 md:p-10">
                <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center mb-5 group-hover:bg-white/20 transition-colors">
                  <TrendingUp className="h-6 w-6 text-teal-300" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2" style={{ fontFamily: 'var(--font-heading)' }}>Term Loans</h3>
                <p className="text-sm text-slate-300 max-w-sm mb-4">SBA 7(a), 10 year growth loans, and 3 to 5 year business term loans. Fixed rates, predictable payments.</p>
                <div className="flex flex-wrap gap-2 mb-5">
                  {['3 to 10 Year Terms', 'Up to $350K', 'From Prime + 2.75%'].map((t) => (
                    <span key={t} className="text-[11px] font-semibold bg-white/10 backdrop-blur-sm text-slate-200 px-3 py-1 rounded-full">{t}</span>
                  ))}
                </div>
                <div className="text-sm font-semibold text-teal-300 group-hover:text-teal-200 transition-colors flex items-center gap-1">
                  Explore programs <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>

            {/* Lines of Credit + Equipment stacked */}
            <div className="md:col-span-5 flex flex-col gap-5">
              <Link to="/solutions/lines-of-credit" data-testid="bento-lines-of-credit" className="group relative rounded-2xl overflow-hidden flex-1 min-h-[160px] flex flex-col justify-end">
                <div className="absolute inset-0">
                  <img src="https://images.unsplash.com/photo-1734503937317-3b88a42ac50c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMGdyb3d0aCUyMGNoYXJ0JTIwZmluYW5jZXxlbnwwfHx8fDE3NzQ1MjQ4MTR8MA&ixlib=rb-4.1.0&q=85" alt="" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-blue-950 via-blue-950/80 to-blue-950/30" />
                </div>
                <div className="relative z-10 p-7">
                  <div className="w-11 h-11 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center mb-4 group-hover:bg-white/20 transition-colors">
                    <Shield className="h-5 w-5 text-blue-300" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-1.5" style={{ fontFamily: 'var(--font-heading)' }}>Lines of Credit</h3>
                  <p className="text-sm text-blue-100/70 leading-relaxed">Up to $250K unsecured. Draw what you need, pay only on what you use.</p>
                  <div className="mt-3 text-sm font-semibold text-blue-300 group-hover:text-blue-200 flex items-center gap-1">
                    Learn more <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>

              <Link to="/solutions/equipment-financing" data-testid="bento-equipment" className="group relative rounded-2xl overflow-hidden flex-1 min-h-[160px] flex flex-col justify-end">
                <div className="absolute inset-0">
                  <img src="https://images.pexels.com/photos/1579356/pexels-photo-1579356.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-amber-950 via-amber-950/80 to-amber-950/30" />
                </div>
                <div className="relative z-10 p-7">
                  <div className="w-11 h-11 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center mb-4 group-hover:bg-white/20 transition-colors">
                    <Wrench className="h-5 w-5 text-amber-300" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-1.5" style={{ fontFamily: 'var(--font-heading)' }}>Equipment Financing</h3>
                  <p className="text-sm text-amber-100/70 leading-relaxed">Fund machinery, vehicles, and technology. Equipment serves as collateral.</p>
                  <div className="mt-3 text-sm font-semibold text-amber-300 group-hover:text-amber-200 flex items-center gap-1">
                    Learn more <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            </div>

            {/* Jumbo + Hybrid + Merchant row */}
            <Link to="/solutions/jumbo-term-loan" data-testid="bento-jumbo" className="md:col-span-4 group relative rounded-2xl overflow-hidden min-h-[220px] flex flex-col justify-end">
              <div className="absolute inset-0">
                <img src="https://images.pexels.com/photos/1486222/pexels-photo-1486222.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Jumbo" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/80 to-slate-950/20" />
              </div>
              <div className="relative z-10 p-7">
                <div className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center mb-3 group-hover:bg-white/20 transition-colors">
                  <Building2 className="h-5 w-5 text-sky-300" />
                </div>
                <h3 className="text-lg font-bold text-white mb-1" style={{ fontFamily: 'var(--font-heading)' }}>Jumbo Term Loan</h3>
                <p className="text-xs text-slate-300 leading-relaxed mb-2">Bridge financing up to $25M. Close and fund in as little as 1 day.</p>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {['Up to $25M', '1 Day Close'].map((t) => (
                    <span key={t} className="text-[9px] font-semibold bg-white/10 backdrop-blur-sm text-slate-200 px-2 py-0.5 rounded-full">{t}</span>
                  ))}
                </div>
                <div className="text-xs font-semibold text-sky-300 group-hover:text-sky-200 flex items-center gap-1">
                  Learn more <ArrowRight className="h-3 w-3 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>

            <Link to="/solutions/hybrid-loc" data-testid="bento-hybrid" className="md:col-span-4 group relative rounded-2xl overflow-hidden min-h-[220px] flex flex-col justify-end">
              <div className="absolute inset-0">
                <img src="https://images.pexels.com/photos/3184306/pexels-photo-3184306.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Hybrid" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-violet-950 via-violet-950/80 to-violet-950/20" />
              </div>
              <div className="relative z-10 p-7">
                <div className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center mb-3 group-hover:bg-white/20 transition-colors">
                  <Layers className="h-5 w-5 text-violet-300" />
                </div>
                <h3 className="text-lg font-bold text-white mb-1" style={{ fontFamily: 'var(--font-heading)' }}>Hybrid Term / LOC</h3>
                <p className="text-xs text-slate-300 leading-relaxed mb-2">Up to $25M. Pay interest only on what you draw. Close in 1 day.</p>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {['Up to $25M', 'Interest Only'].map((t) => (
                    <span key={t} className="text-[9px] font-semibold bg-white/10 backdrop-blur-sm text-slate-200 px-2 py-0.5 rounded-full">{t}</span>
                  ))}
                </div>
                <div className="text-xs font-semibold text-violet-300 group-hover:text-violet-200 flex items-center gap-1">
                  Learn more <ArrowRight className="h-3 w-3 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>

            {/* Merchant Processing */}
            <Link to="/solutions/merchant-processing" data-testid="bento-merchant" className="md:col-span-4 group relative rounded-2xl overflow-hidden min-h-[220px] flex flex-col justify-end">
              <div className="absolute inset-0">
                <img src="https://images.pexels.com/photos/2988232/pexels-photo-2988232.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="w-full h-full object-cover object-center" />
                <div className="absolute inset-0 bg-gradient-to-t from-emerald-950 via-emerald-950/80 to-emerald-950/20" />
              </div>
              <div className="relative z-10 p-7">
                <div className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center mb-3 group-hover:bg-white/20 transition-colors">
                  <CreditCard className="h-5 w-5 text-emerald-300" />
                </div>
                <h3 className="text-lg font-bold text-white mb-1" style={{ fontFamily: 'var(--font-heading)' }}>Merchant Processing</h3>
                <p className="text-xs text-slate-300 leading-relaxed mb-2">Reduce hidden fees and improve margins. Powered by CardConnect, a Fiserv company.</p>
                <div className="text-xs font-semibold text-emerald-300 group-hover:text-emerald-200 flex items-center gap-1">
                  Learn more <ArrowRight className="h-3 w-3 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>

            {/* Real Estate Financing - full width featured */}
            <Link to="/solutions/real-estate" data-testid="bento-real-estate" className="md:col-span-12 group relative rounded-2xl overflow-hidden min-h-[200px]">
              <div className="absolute inset-0">
                <img src="https://images.pexels.com/photos/323780/pexels-photo-323780.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="w-full h-full object-cover object-center" />
                <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/85 to-slate-950/50" />
              </div>
              <div className="relative z-10 p-8 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div className="flex items-start gap-5">
                  <div className="w-12 h-12 rounded-xl bg-amber-500/15 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                    <Building className="h-6 w-6 text-amber-300" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-1.5" style={{ fontFamily: 'var(--font-heading)' }}>Real Estate Financing</h3>
                    <p className="text-sm text-slate-300 max-w-lg">$250K to $2B across the USA and Caribbean. Refinances, new developments, structured deals, acquisitions, and bridge financing.</p>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {['$250K to $2B', 'USA + Caribbean', 'All Asset Types'].map((t) => (
                        <span key={t} className="text-[10px] font-semibold bg-white/10 backdrop-blur-sm text-slate-200 px-2.5 py-1 rounded-full">{t}</span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex-shrink-0 text-sm font-semibold text-amber-300 group-hover:text-amber-200 flex items-center gap-1">
                  Learn more <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>

            {/* SBA Programs row */}
            <Link to="/solutions/sba-grocery" data-testid="bento-sba-grocery" className="md:col-span-6 group relative rounded-2xl overflow-hidden min-h-[180px] flex flex-col justify-end">
              <div className="absolute inset-0">
                <img src="https://images.pexels.com/photos/264636/pexels-photo-264636.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Grocery" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0C2340] via-[#0C2340]/80 to-[#0C2340]/20" />
              </div>
              <div className="relative z-10 p-7">
                <div className="inline-flex items-center gap-1.5 bg-amber-500/20 rounded-full px-2.5 py-0.5 mb-3">
                  <Shield className="h-3 w-3 text-amber-300" />
                  <span className="text-[9px] font-bold text-amber-200 uppercase tracking-wider">SBA Backed</span>
                </div>
                <h3 className="text-lg font-bold text-white mb-1" style={{ fontFamily: 'var(--font-heading)' }}>SBA Grocery Program</h3>
                <p className="text-xs text-slate-300 leading-relaxed mb-2">Government backed financing for agriculture, food distribution, and grocery retail. 10 year terms, lower payments.</p>
                <div className="text-xs font-semibold text-amber-300 group-hover:text-amber-200 flex items-center gap-1">
                  Check eligibility <ArrowRight className="h-3 w-3 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>

            <Link to="/solutions/sba-made-in-america" data-testid="bento-sba-mia" className="md:col-span-6 group relative rounded-2xl overflow-hidden min-h-[180px] flex flex-col justify-end">
              <div className="absolute inset-0">
                <img src="https://images.pexels.com/photos/1108101/pexels-photo-1108101.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Manufacturing" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a2e] via-[#1a1a2e]/80 to-[#1a1a2e]/20" />
              </div>
              <div className="relative z-10 p-7">
                <div className="inline-flex items-center gap-1.5 bg-red-500/20 rounded-full px-2.5 py-0.5 mb-3">
                  <Shield className="h-3 w-3 text-red-300" />
                  <span className="text-[9px] font-bold text-red-200 uppercase tracking-wider">SBA Backed</span>
                </div>
                <h3 className="text-lg font-bold text-white mb-1" style={{ fontFamily: 'var(--font-heading)' }}>SBA Made in America</h3>
                <p className="text-xs text-slate-300 leading-relaxed mb-2">Government backed financing for U.S. manufacturing. Support production, scaling, and operational growth.</p>
                <div className="text-xs font-semibold text-red-300 group-hover:text-red-200 flex items-center gap-1">
                  Check eligibility <ArrowRight className="h-3 w-3 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>

            {/* Transportation Finance + Management Systems row */}
            <Link to="/solutions/transportation-finance" data-testid="bento-transportation" className="md:col-span-7 group relative rounded-2xl overflow-hidden min-h-[200px] flex flex-col justify-end">
              <div className="absolute inset-0">
                <img src="https://images.pexels.com/photos/2199293/pexels-photo-2199293.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Transportation" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/80 to-slate-950/20" />
              </div>
              <div className="relative z-10 p-8 md:p-10">
                <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center mb-5 group-hover:bg-white/20 transition-colors">
                  <Truck className="h-6 w-6 text-orange-300" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'var(--font-heading)' }}>Transportation Finance</h3>
                <p className="text-sm text-slate-300 max-w-md mb-4">Revolving credit lines, equipment financing, and cash out solutions for trucking companies and transportation businesses.</p>
                <div className="text-sm font-semibold text-orange-300 group-hover:text-orange-200 transition-colors flex items-center gap-1">
                  Explore Transportation Finance <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>

            <Link to="/solutions/management-systems" data-testid="bento-management" className="md:col-span-5 group relative rounded-2xl overflow-hidden min-h-[200px] flex flex-col justify-end">
              <div className="absolute inset-0">
                <img src="https://images.pexels.com/photos/3183150/pexels-photo-3183150.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Management" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-indigo-950 via-indigo-950/80 to-indigo-950/20" />
              </div>
              <div className="relative z-10 p-8">
                <div className="w-11 h-11 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center mb-4 group-hover:bg-white/20 transition-colors">
                  <Monitor className="h-5 w-5 text-indigo-300" />
                </div>
                <h3 className="text-xl font-bold text-white mb-1.5" style={{ fontFamily: 'var(--font-heading)' }}>Intelligent Management Systems</h3>
                <p className="text-sm text-indigo-100/70 leading-relaxed">Quidity PMS, Orchard LMS, and Clover POS. Custom consulting and implementation.</p>
                <div className="mt-3 text-sm font-semibold text-indigo-300 group-hover:text-indigo-200 flex items-center gap-1">
                  Learn more <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* ====== 10-YEAR LOAN + CALCULATOR ====== */}
      <section className="py-24 md:py-32 bg-slate-900 text-white relative noise-overlay overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-teal-500/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-blue-500/10 rounded-full blur-[100px]" />

        <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-flex items-center gap-2 bg-teal-500/10 border border-teal-500/20 rounded-full px-4 py-1.5 mb-6">
                <Zap className="h-3.5 w-3.5 text-teal-400" />
                <span className="text-xs font-semibold text-teal-300 uppercase tracking-wider">Featured Program</span>
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
                Quidity Direct<br />10 Year Non SBA<br />Term Loan
              </h2>
              <p className="text-slate-400 leading-relaxed mb-8 max-w-md">
                Refinance MCA debt, access larger loan amounts, and lock in a 10 year repayment term with no SBA approval required.
              </p>
              <div className="space-y-4 mb-8">
                {[
                  'Loan amounts $25K to $200K',
                  'Refinance MCA debt into long term stability',
                  'Fast approvals, no government process',
                  '640+ credit score, 1+ years in business',
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-teal-400 flex-shrink-0" />
                    <span className="text-sm text-slate-300">{item}</span>
                  </div>
                ))}
              </div>
              <Link
                to="/apply"
                data-testid="featured-apply-button"
                className="inline-flex items-center gap-2 bg-teal-600 text-white font-semibold text-sm px-7 py-3.5 rounded-full hover:bg-teal-500 transition-colors"
              >
                Get Started <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            {/* Calculator */}
            <div className="bg-white/[0.05] backdrop-blur-sm border border-white/10 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                Estimate Your Loan
              </h3>
              <p className="text-sm text-slate-400 mb-8">
                Enter your average monthly revenue to see estimated loan amount and payment.
              </p>

              <div className="mb-6">
                <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Average Monthly Revenue</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-semibold">$</span>
                  <input
                    data-testid="calculator-revenue-input"
                    type="number"
                    value={monthlyRevenue}
                    onChange={(e) => setMonthlyRevenue(e.target.value)}
                    placeholder="0"
                    className="w-full bg-white/10 border border-white/20 text-white text-2xl font-bold rounded-xl pl-8 pr-4 py-4 placeholder:text-slate-600 focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-2">Minimum annual revenue: $100,000</p>
              </div>

              {monthlyRevenue && !isNaN(monthlyRevenue) && parseFloat(monthlyRevenue) > 0 && (
                <div className="space-y-4 animate-fade-in" style={{ animationDuration: '0.3s' }}>
                  {isEligible ? (
                    <>
                      <div className="bg-teal-500/10 border border-teal-500/20 rounded-xl p-6 text-center">
                        <div data-testid="calculator-loan-amount" className="text-4xl font-bold text-teal-400 mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
                          ${loanAmount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </div>
                        <span className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Estimated Loan Amount</span>
                      </div>
                      <div className="bg-white/5 border border-white/10 rounded-xl p-6 text-center">
                        <div data-testid="calculator-monthly-payment" className="text-3xl font-bold text-white mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
                          ${monthlyPayment.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                        <span className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Estimated Monthly Payment</span>
                      </div>
                    </>
                  ) : (
                    <div className="bg-slate-800 rounded-xl p-6 text-center">
                      <p className="text-sm text-slate-400">Minimum annual revenue of $100,000 required. Enter at least $8,334/month.</p>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-8 pt-6 border-t border-white/10">
                <h4 className="text-sm font-bold text-white mb-3">Documents You'll Need</h4>
                <div className="space-y-2">
                  {['Most Recent Business Tax Return', 'Business Bank Statements for the past six months', 'Business Debt Schedule, if applicable'].map((doc) => (
                    <div key={doc} className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-teal-500 flex-shrink-0" />
                      <span className="text-xs text-slate-400">{doc}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ====== HOW IT WORKS ====== */}
      <section className="py-24 md:py-32 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center mb-20">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#115E59] mb-4 block">Process</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>
              From Application to Funding
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 relative">
            <div className="hidden md:block absolute top-8 left-[12.5%] right-[12.5%] h-px bg-slate-200" />
            {[
              { icon: <Zap className="h-6 w-6" />, step: '01', title: 'Apply', desc: 'Complete our streamlined application in minutes.' },
              { icon: <Clock className="h-6 w-6" />, step: '02', title: 'Review', desc: 'Our team reviews your docs and financials fast.' },
              { icon: <Users className="h-6 w-6" />, step: '03', title: 'Approval', desc: 'Get a decision with our fast underwriting process.' },
              { icon: <TrendingUp className="h-6 w-6" />, step: '04', title: 'Funding', desc: 'Receive capital and start growing immediately.' },
            ].map((s) => (
              <div key={s.step} className="text-center relative z-10">
                <div className="w-16 h-16 rounded-2xl bg-slate-900 text-white flex items-center justify-center mx-auto mb-6">
                  {s.icon}
                </div>
                <div className="text-xs font-bold text-[#115E59] uppercase tracking-widest mb-2">Step {s.step}</div>
                <h3 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>{s.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ====== DUAL CTA ====== */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#115E59] mb-4 block">Work With Us</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>
              Whether You Need Capital or<br className="hidden sm:block" /> Refer Clients, We're Ready
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Borrowers */}
            <div className="bento-card bg-[#FAFAFA] overflow-hidden group">
              <div className="p-10 md:p-12">
                <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#115E59] mb-4 block">For Borrowers</span>
                <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                  Get the Funding<br />Your Business Needs
                </h3>
                <p className="text-slate-500 leading-relaxed mb-8">
                  Fast approvals, competitive rates, and dedicated support from application to funding.
                </p>
                <ul className="space-y-3 mb-8">
                  {['Term loans from 3 to 10 years', 'Revolving lines of credit', 'Equipment and machinery financing', 'Minimum 640 credit score'].map((item) => (
                    <li key={item} className="flex items-center gap-3 text-sm text-slate-600">
                      <CheckCircle className="h-4 w-4 text-[#115E59] flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/apply"
                  data-testid="borrower-apply-cta"
                  className="inline-flex items-center gap-2 bg-slate-900 text-white font-semibold text-sm px-7 py-3.5 rounded-full hover:bg-slate-800 transition-all"
                >
                  Start Your Application <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>

            {/* Partners */}
            <div className="relative rounded-2xl overflow-hidden group" style={{ border: '1px solid var(--border)' }}>
              <div className="absolute inset-0 bg-gradient-to-br from-slate-900 to-teal-950/80" />
              <div className="absolute top-0 right-0 w-60 h-60 bg-teal-500/10 rounded-full blur-[80px]" />
              <div className="relative z-10 p-10 md:p-12 text-white">
                <span className="text-xs font-bold uppercase tracking-[0.25em] text-teal-300 mb-4 block">For Referral Partners</span>
                <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                  Grow Your Business<br />With Ours
                </h3>
                <p className="text-slate-300 leading-relaxed mb-8">
                  Join our referral network and earn competitive commissions while providing your clients with top-tier financing solutions.
                </p>
                <ul className="space-y-3 mb-8">
                  {['Competitive commission structure', 'Dedicated partner support', 'Real time deal tracking', 'Co branded marketing materials'].map((item) => (
                    <li key={item} className="flex items-center gap-3 text-sm text-slate-300">
                      <CheckCircle className="h-4 w-4 text-teal-400 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/partner"
                  data-testid="partner-cta"
                  className="inline-flex items-center gap-2 bg-white text-slate-900 font-semibold text-sm px-7 py-3.5 rounded-full hover:bg-slate-100 transition-all"
                >
                  Join the Partner Program <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ====== TESTIMONIALS ====== */}
      <section className="py-24 md:py-32 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#115E59] mb-4 block">Testimonials</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>
              Trusted by Businesses Nationwide
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {mockTestimonials.map((t, i) => (
              <div key={i} className="bento-card bg-white p-8">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <svg key={j} className="w-4 h-4 text-amber-400 fill-current" viewBox="0 0 20 20">
                      <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                    </svg>
                  ))}
                </div>
                <p className="text-slate-600 text-sm leading-relaxed mb-6 italic">"{t.quote}"</p>
                <div>
                  <div className="text-sm font-bold text-slate-900">{t.name}</div>
                  <div className="text-xs text-slate-500">{t.company}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ====== FINAL CTA ====== */}
      <section className="relative py-24 md:py-32 overflow-hidden bg-slate-950">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(17,94,89,0.2)_0%,_transparent_70%)]" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-teal-500/5 rounded-full blur-[150px]" />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-8 text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
            Ready to Engineer<br />Better Cashflow?
          </h2>
          <p className="text-lg text-slate-400 mb-10 max-w-xl mx-auto">
            Whether you're seeking financing or looking to partner with us, we're here to help you grow.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/apply"
              data-testid="final-apply-cta"
              className="group inline-flex items-center gap-2 bg-white text-slate-900 font-semibold text-sm px-8 py-4 rounded-full hover:shadow-[0_0_30px_rgba(94,234,212,0.3)] transition-all duration-300"
            >
              Apply for Financing <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/partner"
              data-testid="final-partner-cta"
              className="inline-flex items-center gap-2 bg-white/[0.07] border border-white/10 text-white font-semibold text-sm px-8 py-4 rounded-full hover:bg-white/[0.12] transition-all duration-300"
            >
              Become a Partner
            </Link>
            <Link
              to="/contact"
              data-testid="final-contact-cta"
              className="inline-flex items-center gap-2 text-slate-400 font-semibold text-sm px-8 py-4 hover:text-white transition-colors"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Homepage;
